// Core libraries
import axios from 'axios';
import moment from 'moment';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

// Third-party libraries
import { IconButton } from '@material-ui/core';
import {
  AttachFile as AttachFileIcon,
  // Language as SearchIcon,
  ClearAll as ClearAllIcon,
  CloseRounded,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  Edit as EditIcon,
  StarBorder as EmptyStarIcon,
  FileCopy,
  Star as FillStarIcon,
  Help as HelpIcon,
  LibraryBooks as LibraryBooksIcon,
  Map as MapIcon,
  Person as PersonIcon,
  Print as PrintIcon,
  Save as SaveIcon,
  SendRounded,
  Update as UpdateIcon
} from '@material-ui/icons';
import { jsPDF } from 'jspdf';
import { Button, Card, Modal, Spinner } from 'react-bootstrap';
import ReactTooltip from 'react-tooltip';
import styled from 'styled-components';

// Local imports
import HeaderEnviroAILogo from '../../assets/header-enviroai-logo.svg';
// import ViewTools from '../../assets/view-tools.svg';
// import VoiceChat from '../../assets/voice-chat.svg';
import { IAuthContext } from '../../context/auth/auth-context';
import withAuthContext from '../../context/auth/AuthConsumer';
import { saveChats, saveProfile, savePrompts } from '../../services/firebase';
import { clearSavedChatData, removeGlobalFlag, updateGlobalFlags, updateQuickPrompts, updateSavedChatData } from '../../store/aiearth/action';
import showToastMessage from '../../util/showToastMessage';
import Profile from '../home/Profile';
import SavedLibrary from '../home/SavedLibrary';
import SavedPrompts from '../home/SavedPrompts';
import { isExxonMobileEmail } from '../Validator';
import ChatTitleList from './ChatTitleList';
import './index.scss';
import LibraryQuestionsListModal from './LibraryQuestionsListModal';
import MessageLoader from './MessageLoader';
import { SpinnerText } from '../../components/custom-styled-components/CommonStyledComponents';
import JurisdictionChip from '../../components/JurisdictionChip';
import ReactGA from 'react-ga4';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import mountainHeroBg from '../../assets/mountain-6.jpg';
import { authFetch } from '../../services/authClient';
import { FileDropOverlay } from '../../components/ui';
import useFileDrop from '../../hooks/useFileDrop';
import { ACCEPT_ATTR, IMAGE_MIME_TYPES, partitionFiles } from '../../utils/fileAccept';
import notifyRejectedFiles from '../../utils/notifyRejectedFiles';

// Fixed column per model, regardless of which response finishes first.
// enviroNumber: 1 = Gemini, 2 = GPT/OpenAI, 3 = Claude/Anthropic.
// Slots:        GPT -> #1 (a1),  Gemini -> #2 (a2),  Claude -> #3 (a3).
const SLOT_BY_ENVIRO_NUMBER: Record<number, number> = { 1: 2, 2: 1, 3: 3 };

type DraftStreamHandlers = {
  onStatus?: (message: string) => void;
  onMeta?: (chatId: number | string | null) => void;
  onToken: (chunk: string) => void;
  onDone: (chatId: number | string | null) => void;
  onError: (message: string) => void;
};

// SSE reader for the gpt/gemini/claude get_pred endpoints. The backend emits
// line-buffered `data: {…}` events with `type: status | meta | token | done |
// error`, mirroring enviro-chat's classify_route/stream convention.
const streamDraftResponse = async (
  url: string,
  formData: FormData,
  handlers: DraftStreamHandlers,
  signal?: AbortSignal
): Promise<void> => {
  const res = await authFetch(url, { method: 'POST', body: formData, signal });
  if (!res.ok) {
    if (res.status === 413) throw new Error('FILE_TOO_LARGE');
    throw new Error(`HTTP ${res.status}`);
  }
  if (!res.body) throw new Error('No response body');

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let carryOver = '';
  let streamDone = false;
  let lastChatId: number | string | null = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    if (signal?.aborted) break;

    const chunk = carryOver + decoder.decode(value, { stream: true });
    const lines = chunk.split('\n');
    carryOver = lines.pop() ?? '';

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      let event: any;
      try {
        event = JSON.parse(line.slice(6));
      } catch {
        continue;
      }

      if (event.type === 'status') {
        handlers.onStatus?.(event.message ?? '');
      } else if (event.type === 'meta') {
        if (event.chat_id != null) lastChatId = event.chat_id;
        handlers.onMeta?.(event.chat_id ?? null);
      } else if (event.type === 'token') {
        handlers.onToken(event.content ?? '');
      } else if (event.type === 'done') {
        streamDone = true;
        if (event.chat_id != null) lastChatId = event.chat_id;
        handlers.onDone(lastChatId);
      } else if (event.type === 'error') {
        streamDone = true;
        handlers.onError(event.message ?? 'Server error');
      }
    }
  }

  // Stream closed with no explicit done/error (e.g. dropped connection):
  // finalize with whatever was received so the UI does not hang.
  if (!streamDone && !signal?.aborted) {
    handlers.onDone(lastChatId);
  }
};

const StyledTooltip = styled(ReactTooltip)`
  max-width: 15% !important
`;

const MountainBackdrop = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  background-image: url(${mountainHeroBg});
  background-size: cover;
  background-position: center 30%;
  pointer-events: none;
`;

const MountainOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.55) 0%, rgba(0, 0, 0, 0.65) 45%, rgba(0, 0, 0, 0.75) 100%);
  pointer-events: none;
`;

const MountainContentWrap = styled.div<{ $landing?: boolean }>`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  height: 100%;
  overflow-x: hidden;
  overflow-y: ${(p) => (p.$landing ? 'hidden' : 'auto')};
  justify-content: flex-start;
  align-items: ${(p) => (p.$landing ? 'center' : 'stretch')};
  padding-top: ${(p) => (p.$landing ? 'clamp(8vh, 12vh, 16vh)' : '0')};
  gap: ${(p) => (p.$landing ? '20px' : '20px')};
`;

const HeroWelcome = styled.div<{ $landing?: boolean }>`
  flex: ${(p) => (p.$landing ? '0 0 auto' : '1 1 auto')};
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: ${(p) => (p.$landing ? 'center' : 'flex-start')};
  text-align: center;
  padding: ${(p) =>
    p.$landing ? '0 1rem' : 'clamp(1.25rem, 5vh, 3rem) 1rem 1rem'};
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: ${(p) => (p.$landing ? 'visible' : 'auto')};
`;

const ChatInputDock = styled.div<{ $compact?: boolean; $landing?: boolean }>`
  flex-shrink: 0;
  width: 100%;
  max-width: ${(p) => (p.$landing ? 'min(800px, 70vw)' : 'none')};
  margin: ${(p) => (p.$landing ? '0 auto' : '0')};
  padding-bottom: ${(p) => (p.$compact ? '8px' : '12px')};
  box-sizing: border-box;
`;

const HeroTagline = styled.p`
  margin: 0 0 16px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #5eead4;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', sans-serif;
`;

const HeroHeading = styled.h1`
  margin: 0 0 12px;
  font-family: 'Instrument Serif', Georgia, serif;
  font-size: clamp(1.85rem, 3.2vw, 2.75rem);
  font-weight: 400;
  line-height: 1.2;
  color: #fafafa;
`;

const HeroHeadingAccent = styled.span`
  display: block;
  margin-top: 4px;
  font-style: italic;
  color: #34d399;
`;

const SuggestionChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px 12px;
  max-width: 750px;
  width: 100%;
  margin-top: 6px;
  margin-bottom: 0;
`;

const SuggestionChipBtn = styled.button`
  appearance: none;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(10, 10, 12, 0.55);
  color: #94a3b8;
  font-size: 14px;
  padding: 10px 18px;
  border-radius: 999px;
  cursor: pointer;
  font-family: inherit;
  backdrop-filter: blur(8px);
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: rgba(52, 211, 153, 0.12);
    border-color: rgba(52, 211, 153, 0.35);
    color: #e2e8f0;
  }
`;

const FALLBACK_CHIPS = [
  'What are the latest EPA air quality rules?',
  'How do I interpret stack test results?',
  'Summarize TRI reporting for my facility',
  'What is a Title V permit?',
];

interface IFavorite {
  id: string;
  question: string;
  isFavorite: boolean;
}

interface ILibraryFavorite {
  id: string;
  question: string;
  isFavorite: boolean;
}

const EnviroAI: React.FC<{ context: IAuthContext }> = (props) => {
  const history = useHistory();
  const location = useLocation();
  const context = props.context;
  const [currentChat, setCurrentChat] = useState<number | null>(null);
  const [savedChats, setSavedChats]: any = useState([]);
  const [showSpinner, setShowSpinner] = useState<string | null>(null);
  const [messages, setMessages]: any[] = useState([]);
  const [searchWord, setSearchWord] = useState('');
  const [isNewChatEnable, setIsNewChatEnable] = useState(true);
  const [newChat, setNewChat]: any = useState(null);
  const [savedChatId, setSavedChatId] = useState(null);
  // const [showDraftBtn, setShowDraftBtn] = useState(false);
  const [showSaveBtn, setShowSaveBtn] = useState(false);
  const [showSavePromptBtn, setShowSavePromptBtn] = useState(false);
  const [showSaveLibraryBtn, setShowSaveLibraryBtn] = useState(false);
  const [favorites, setFavorites] = useState<IFavorite[]>([]);
  const [libraryFavorites, setLibraryFavorites] = useState<ILibraryFavorite[]>([]);
  const [showQuestionsPopup, setShowQuestionsPopup] = useState(false);
  const [showLibraryQuestionsPopup, setShowLibraryQuestionsPopup] = useState(false);
  const [prompts, setPrompts] = useState<any[]>([]);
  const [showSavePrompt, setShowSavePrompt] = useState(false);
  const [showSaveLibrary, setShowSaveLibrary] = useState(false);
  const [showSavePrivateLibrary, setShowSavePrivateLibrary] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showDeleteConfitmationPopup, setShowDeleteConfitmationPopup] = useState(false);
  const [activeProfile, setActiveProfile] = useState<string | null>(null);
  const [isChatUpdate, setIsChatUpdate] = useState(false);
  const [editableIndex, setEditableIndex] = useState<number | any>(null);
  const [totalSavedChats, setTotalSavedChats] = useState(0);
  const [mainQuestion, setMainQuestion] = useState('');
  // totalDraftLoaded use to keep 3 drafts loading synchronizely
  const [totalDraftLoaded, setTotalDraftLoaded] = useState(3);
  const [files, setFiles]: any[] = useState([]);
  const [filePreviews, setFilePreviews]: any[] = useState([]);
  const store: any = useSelector<any>((state: any): any => state);
  const textareaRef: any = useRef(null);
  const [isSearchMultiline, setIsSearchMultiline] = useState(false);
  const chatIdsRef = useRef<Record<number, number | string>>({});
  // Aborts any in-flight draft streams when a new question starts (or on unmount)
  // so stale tokens never leak into the current question's slots.
  const draftAbortRef = useRef<AbortController | null>(null);
  const consumedHomepageQueryRef = useRef(false);
  const searchInBotRef = useRef<
    ((e: any, isSuggestedQuestion: boolean) => void | Promise<void>) | null
  >(null);

  const dispatch = useDispatch();
  useEffect(() => {
    (async () => {
      Promise.all([
        getSavedChats({ type: 'getAll' }),
        getPrompts(),
        getProfileData()
      ]);

      // if(localStorage.getItem('previousPage') || localStorage.getItem('isBackToEnviroPage') || store.savedChat) {
      // localStorage.removeItem('previousPage');
      // localStorage.removeItem('isBackToEnviroPage');
      // dispatch(updateSavedChatData({}));
      // }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useEffect(() => {
  //   // tslint:disable-next-line:no-console
  //   console.log('savedChat', savedChat);

  //   if(savedChat && savedChat.currentChat >= 0) {
  //     setCurrentChat(savedChat.currentChat);
  //   }
  // }, [savedChat]);

  useEffect(() => {
    // tslint:disable-next-line:no-console
    console.log(store.savedChat, isNewChatEnable);

    if (localStorage.getItem('isBackToEnviroPage') && store.savedChat && (store.savedChat.newChat || store.savedChat.currentChat >= 0)) {
      if (store.savedChat.currentChat >= 0) {
        setCurrentChat(store.savedChat.currentChat);
        // setMessages(store.savedChat.newChat);
      }
      handlePersistantChat();
      return;
    } else {
      // setCurrentChat(null);
      clearPreviousChatData();
    }
    /** when click on new chat button */
    if (isNewChatEnable) {
      resetChatState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewChatEnable]);

  /** Clears previous chat data and updates state */
  const clearPreviousChatData = () => {
    dispatch(removeGlobalFlag('previousPage'));
    localStorage.removeItem('isBackToEnviroPage');
    dispatch(updateSavedChatData({}));
  };

  /** Resets the chat state for a new chat session */
  const resetChatState = () => {
    setCurrentChat(null);
    setNewChat([]);
    setSavedChatId(null);
    setMessages([]);
    chatIdsRef.current = {};
    setFilePreviews([]);
    setFiles([]);
    setShowSaveBtn(false);
    setShowSavePromptBtn(false);
    setShowSaveLibraryBtn(false);
    setShowSavePrivateLibrary(false);
    setEditableIndex(null);
    setTotalDraftLoaded(3);
    setIsChatUpdate(false);
  };

  useEffect(() => {
    chatIdsRef.current = {};
  }, [currentChat]);

  useEffect(() => {
    if (currentChat === null || (store.savedChat.newChat && store.savedChat.currentChat >= 0)) { return; }

    handleNewChatToggle();
    loadCurrentChatData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentChat, savedChats]);

  /** Handles the toggling and state reset for new chats */
  const handleNewChatToggle = () => {
    setNewChat(null);
    setIsNewChatEnable(false);
  };

  /** Loads the current chat data by ID or payload */
  const loadCurrentChatData = () => {
    if (currentChat === null) { return; }
    const currentChatData = savedChats[currentChat];
    if (store.savedChat.newChat) {
      setMessages(store.savedChat.newChat.chatData);
      // Restore chatIds from persistent chat data
      if (store.savedChat.newChat.chatIds) {
        chatIdsRef.current = { ...store.savedChat.newChat.chatIds };
      }
    } else {
      if (currentChatData && currentChatData.id) {
        getChatById({ type: 'get', id: currentChatData.id });
      } else if (currentChatData && currentChatData.payload) {
        setMessages(currentChatData.payload.chatData);
        // Restore chatIds from payload when loading directly
        if (currentChatData.payload.chatIds) {
          chatIdsRef.current = { ...currentChatData.payload.chatIds };
        }
      }
    }
  };

  useEffect(() => {
    if (!currentChat) {
      updateFavorites();
      updateLibraryFavorites();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  /** Updates the favorites list based on the messages */
  const updateFavorites = () => {
    const favoritesList: any = generateQuestionsList(messages, favorites);
    setFavorites(favoritesList);
  };

  /** Updates the library favorites list based on the messages */
  const updateLibraryFavorites = () => {
    const libraryFavoritesList: any = generateQuestionsList(messages, libraryFavorites);
    setLibraryFavorites(libraryFavoritesList);
  };

  /**
   * Generates a list of questions with their favorite status
   * @param {Array} chats - The list of chats
   * @param {Array} favoritesData - The list of favorites or library favorites
   * @returns {Array} The list of questions with favorite status
   */
  const generateQuestionsList = (chats: any[], favoritesData: any[]) => {
    const lastIndex = chats.length - 1;

    return Array.isArray(chats) && chats.map((item: any, i: number) => ({
      id: libraryFavorites[i] && libraryFavorites[i].id ? libraryFavorites[i].id : '',
      question: item.q.title,
      isFavorite: lastIndex !== i && favoritesData.length && favoritesData[i] ? favoritesData[i].isFavorite : false,
    }));
  };

  useEffect(() => {
    handleDraftsLoaded();
    handleFilesReset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalDraftLoaded]);

  /** Handles logic when drafts are loaded */
  const handleDraftsLoaded = () => {
    if (totalDraftLoaded > 2) {
      handleSingleMessageDraft();
      hideDraftLoader();
    }
  };

  /** Shows save buttons if one or more than one messages is loaded */
  const handleSingleMessageDraft = () => {
    if (messages.length >= 1 && currentChat === null) {
      setShowSaveBtn(true);
      setShowSavePromptBtn(true);
      setShowSaveLibraryBtn(true);
    }
  };

  /** Hides the draft loader spinner */
  const hideDraftLoader = () => {
    if (showSpinner === 'view_draft_loader') {
      setShowSpinner('');
    }
  };

  /** Resets the files array when all drafts are loaded */
  const handleFilesReset = () => {
    if (totalDraftLoaded >= 3) {
      setFiles([]);
    }
  };

  const handlePersistantChat = async () => {
    const newChatData = store.savedChat.newChat;
    // tslint:disable-next-line:no-console
    console.log('newChatData====>', newChatData);

    // If there is no saved chat data, exit early
    if (!newChatData || !newChatData.chatData) { return; }

    initializeChat(newChatData);
    clearPreviousSession();
    const lastData = newChatData.chatData[newChatData.chatData.length - 1].q;

    // Determine the loading strategy based on the number of responses
    if (Object.keys(lastData).length === 1) {
      loadAdditionalResponse(lastData.title, newChatData.chatData.length);
    } else if (Object.keys(lastData).length === 2) {
      await loadTwoAdditionalResponses(lastData.title);
    } else if (Object.keys(lastData).length === 3) {
      await loadRemainingResponse(lastData.title, newChatData.chatData.length);
    } else {
      showSaveOptions();
    }

    setIsNewChatEnable(false);
  };

  // Initialize chat data and UI state
  const initializeChat = (newChatData: any) => {
    // tslint:disable-next-line:no-console
    console.log('newChatData.mainTitle=>', newChatData);

    setMainQuestion(newChatData.mainTitle);
    if (newChatData.id) {
      setSavedChatId(newChatData.id);
    }
    // Restore chatIds from saved data to continue conversations properly
    if (newChatData.chatIds) {
      chatIdsRef.current = { ...newChatData.chatIds };
    }
    setTimeout(() => {
      setNewChat(newChatData);
      setMessages(newChatData.chatData);
    });
  };

  // Clear any previous session data
  const clearPreviousSession = () => {
    dispatch(removeGlobalFlag('previousPage'));
    dispatch(updateSavedChatData({}));
    localStorage.removeItem('isBackToEnviroPage');
  };

  // Load additional responses if only one response is loaded
  const loadAdditionalResponse = (title: string, chatLength: number) => {
    searchInBot(title, true);
  };

  // Load two more responses if only one response is already loaded
  const loadTwoAdditionalResponses = async (title: string) => {
    draftAbortRef.current?.abort();
    draftAbortRef.current = new AbortController();
    setTotalDraftLoaded(1);
    const drafts = store.globalFlags.loadedDrafts || [];
    const loaded = drafts[0];
    const draftsLookup: any = {
      1: [2, 3],
      2: [1, 3],
      default: [1, 2]
    };

    const draftsRemain = draftsLookup[loaded] || draftsLookup.default;

    setShowSpinner('view_draft_loader');

    const index = messages.length;
    const question = title || messages[index - 1].q.title;

    let chatStatus = '';
    if (currentChat != null && currentChat >= 0) {
      chatStatus = 'ongoing';
    } else {
      chatStatus = newChat && newChat.chatData && newChat.chatData.length > 0 ? 'ongoing' : 'new';
    }


    const formData = new FormData();
    formData.append('q', question);
    formData.append('chatStatus', chatStatus);

    const localUserId = localStorage.getItem('uid');
    if (localUserId) {
      formData.append('userId', encodeURIComponent(localUserId));
    }
    const enviroChatApi = process.env.REACT_APP_ENVIROCHAT_API_ENDPOINT;
    const getApiEndPoint = (draft: number) => {
      if (draft === 1) { return `${enviroChatApi}/gemini/get_pred`; }
      if (draft === 2) { return `${enviroChatApi}/gpt/get_pred`; }
      return `${enviroChatApi}/claude/get_pred`;
    };

    const apiEndPoints = [
      draftsRemain ? getApiEndPoint(draftsRemain[0]) : `${enviroChatApi}/gpt/get_pred`,
      draftsRemain ? getApiEndPoint(draftsRemain[1]) : `${enviroChatApi}/claude/get_pred`,
    ];

    const method = Array.isArray(files) && files.length ? 'POST' : 'GET';
    const draft1 = draftsRemain ? draftsRemain[0] : 2;
    const draft2 = draftsRemain ? draftsRemain[1] : 3;

    if (method === 'POST') {
      files.forEach((file: any) => formData.append('files', file));
    }

    getDraftData(apiEndPoints[0], draft1, index, formData);
    getDraftData(apiEndPoints[1], draft2, index, formData);
  };

  // Load the remaining response when two responses are already loaded
  const loadRemainingResponse = async (title: string, chatLength: number) => {
    draftAbortRef.current?.abort();
    draftAbortRef.current = new AbortController();
    setTotalDraftLoaded(2);
    const chatStatus = getChatStatus();
    const formData = buildFormData(title, chatStatus);

    // const loadedDrafts = JSON.parse(localStorage.getItem('loadedDrafts') || '[]');
    const loadedDrafts = store.globalFlags.loadedDrafts || [];
    const draftRemain = loadedDrafts.includes(2) && loadedDrafts.includes(3) ? 1 : loadedDrafts.includes(1) && loadedDrafts.includes(3) ? 2 : 3;

    const enviroChatApi = process.env.REACT_APP_ENVIROCHAT_API_ENDPOINT;

    const getApiEndPoint = (draft: number) => {
      const endpoints: any = {
        1: `${enviroChatApi}/gemini/get_pred`,
        2: `${enviroChatApi}/gpt/get_pred`,
        3: `${enviroChatApi}/claude/get_pred`,
      };
      return endpoints[draft];
    };
    const apiEndPoint = getApiEndPoint(draftRemain);
    await getDraftData(apiEndPoint, draftRemain, chatLength, formData);
  };
  // Helper function to determine the chat status
  const getChatStatus = () => {
    if (currentChat != null && currentChat >= 0) {
      return 'ongoing';
    }
    return newChat && newChat.chatData && newChat.chatData.length > 0 ? 'ongoing' : 'new';
  };

  // Show save buttons if all responses are loaded
  const showSaveOptions = () => {
    setShowSaveBtn(true);
    setShowSavePromptBtn(true);
    setShowSaveLibraryBtn(true);
  };

  async function getPrompts() {
    try {
      setShowSpinner('load_question_list');

      const storedPromptsData = Object.values(store.quickPrompts);

      if (storedPromptsData.length > 0) {
        setPrompts(storedPromptsData);
        return;
      }

      const response: any = await savePrompts({ type: 'getAll', pageSize: 4 });

      if (response && response.data) {
        showToastMessage({ type: 'info', title: 'Retrieved prompts successfully.' });

        // Flatten the nested arrays to retrieve prompt data
        const promptsList = response.data.data.flatMap((items: any) => items.payload.promptData).flat(2);
        setPrompts(promptsList);
        dispatch(updateQuickPrompts(promptsList));
      }
    } catch (error) {
      if (error instanceof Error) {
        handleErrorMessage(error);
      }
    } finally {
      setShowSpinner(null);
    }
  }

  async function getChatById(data: any) {
    try {
      setShowSpinner('load_current_chat');
      const response: any = await saveChats(data);

      if (isValidChatResponse(response)) {
        updateSavedChats(response.data.payload.chatData, response.data.payload.chatIds);
      }
    } catch (error) {
      if (error instanceof Error) {
        handleErrorMessage(error);
      }
    } finally {
      setTotalDraftLoaded(3);
      setIsChatUpdate(false);
      setShowSpinner('');
    }
  }

  function isValidChatResponse(response: any): boolean {
    return response && response.data && Array.isArray(response.data.payload.chatData);
  }

  function updateSavedChats(chatData: any[], chatIdsFromResponse?: Record<number, number | string>): void {
    if (currentChat === null || !savedChats[currentChat]) {
      return;
    }

    const currentSavedChat = savedChats[currentChat];

    currentSavedChat.payload.chatData = chatData;
    setMessages(chatData);
    setMainQuestion(currentSavedChat.payload.mainTitle);
    setSavedChatId(savedChats[currentChat].id);

    // Restore chatIds from response or saved payload to continue conversations properly
    if (chatIdsFromResponse) {
      chatIdsRef.current = { ...chatIdsFromResponse };
      // Also update the saved chat payload with chatIds if they exist
      currentSavedChat.payload.chatIds = { ...chatIdsFromResponse };
    } else if (currentSavedChat.payload.chatIds) {
      chatIdsRef.current = { ...currentSavedChat.payload.chatIds };
    }
  }

  /** Get saved chats */
  async function getSavedChats(data: any) {
    try {
      setShowSpinner('load_question_list');

      const response: any = await saveChats({ ...data, pageSize: 15 });

      if (response && response.data) {
        showToastMessage({ type: 'info', title: 'Retrieved saved chats successfully.' });
        const savedChatData: any = response.data.data.map((item: any, i: number) => ({
          ...item,
          index: i
        }));
        setSavedChats(savedChatData);

        setTotalSavedChats(response.data.totalItems);
        return savedChatData;
        // setSavedChatId((id) => {
        //   if(id !== null) {
        //     setCurrentChat(0);
        //   }
        //   return id;
        // });
      }
    } catch (error) {
      if (error instanceof Error) {
        handleErrorMessage(error);
      }
    } finally {
      setShowSpinner(null);
    }
    return null;
  }

  /** Get current active profile */
  const getProfileData = async () => {
    try {
      const response: any = await saveProfile({ type: 'getAll' });

      if (response && response.data && response.data.length) {
        const activeProfileData = response.data[0].payload.profileData.find((item: any) => item.enable === true);
        const activeProfileTitle = activeProfileData ? activeProfileData.title : null;
        setActiveProfile(activeProfileTitle);
      }
    } catch (error) {
      if (error instanceof Error) {
        handleErrorMessage(error);
      }
    }
  };

  const toggleChat = async (id: number) => {
    if (id === currentChat) {
      return;
    }
    setFiles([]);
    setFilePreviews([]);
    dispatch(clearSavedChatData());
    setCurrentChat(id);
  };

  /**
   * Hyperlink generate from plain text
   * @param answer
   * @param index
   * @returns
   */
  // Render an answer as Markdown (GFM) so headings, lists, tables, code, bold
  // etc. are formatted. The first `\n\n`-delimited block is the response label
  // ("EnviroAI Model Response #N - <Model>"); the rest is the model output.
  const renderAnswerContent = (answer: any) => {
    if (!answer) { return null; }
    const answerParts = String(answer).split('\n\n');
    const tag = answerParts[0];
    const message = answerParts.slice(1).join('\n\n');
    return (
      <>
        <p className="label">{tag}</p>
        <div className="answer-description">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              a: ({ href, children }) => (
                <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
              ),
            }}
          >
            {message}
          </ReactMarkdown>
        </div>
      </>
    );
  };
  /**
   * Add new question
   * @param question
   * @returns
   */
  const addQuestion = (question: string) => {
    if (isNewChatEnable) {
      // New chat mode enable
      const payload = { mainTitle: question, chatData: [{ q: { title: question }, attachFiles: filePreviews || [], timestamp: Math.floor(Date.now() / 1000) }] };
      setNewChat(payload);
      setMessages(payload.chatData);
      setIsNewChatEnable(false);
      return;
    }

    if (newChat) {
      // New chat multiple question handler
      setNewChat((chat: any) => {
        const existChatData = Array.isArray(chat.chatData) ? chat.chatData : [];
        const updatedChatData = [...existChatData, { q: { title: question }, attachFiles: filePreviews, timestamp: Math.floor(Date.now() / 1000) }];
        setMessages(updatedChatData);
        return { ...chat, chatData: updatedChatData };
      });
      return;
    }

    if (currentChat === null) {
      return;
    }

    // Add question to the existing chat
    const chatData = savedChats[currentChat].payload.chatData;
    savedChats[currentChat].payload.chatData = [...chatData, { q: { title: question }, attachFiles: filePreviews, timestamp: Math.floor(Date.now() / 1000) }];
    setMessages(savedChats[currentChat].payload.chatData);
  };

  /**
   * Add new answer to chatbot
   * @param answer
   * @param draftNumber
   * @param i
   */
  const addAnswer = (answer: string, draftNumber: number, i: number) => {
    let chatData: any = null;

    // New chat mode
    setNewChat((payload: any) => {
      if (payload && payload.chatData) {
        const length = payload.chatData.length ? payload.chatData.length - 1 : 0;
        chatData = payload.chatData[length];
      }
      return payload;
    });

    if (!chatData) {
      if (currentChat === null) { return; }
      chatData = savedChats[currentChat].payload.chatData[i];
    }

    if (draftNumber === 1) {
      chatData = { ...chatData, q: { ...chatData.q, a1: answer } };
    } else if (draftNumber === 2) {
      chatData = { ...chatData, q: { ...chatData.q, a2: answer } };
    } else {
      chatData = { ...chatData, q: { ...chatData.q, a3: answer } };
    }

    // When new chat is enabled
    if (currentChat === null) {
      setNewChat((chat: any) => {
        if (chat) {
          const length = chat && chat.chatData && chat.chatData.length ? chat.chatData.length - 1 : 0;
          chat.chatData[length] = chatData;
        }
        // Pass a fresh array reference so React re-renders on every streamed
        // flush (the underlying array is mutated in place otherwise, which
        // React bails out of, deferring the update until the stream completes).
        setMessages([...chat.chatData]);
        return chat;
      });
      return;
    }
    savedChats[currentChat].payload.chatData[i] = chatData;
    setMessages([...savedChats[currentChat].payload.chatData]);
  };

  const getModelName = (enviroNumber: number) =>
    enviroNumber === 1 ? 'Gemini Base-Model' : enviroNumber === 2 ? 'OpenAI Base-Model' : 'Anthropic Base-Model';

  const getDraftData = async (url: string = '', enviroNumber: number, index: number, formData: FormData) => {
    const existingChatId = chatIdsRef.current[enviroNumber];

    const requestData = (() => {
      const cloned = new FormData();
      formData.forEach((value: any, key: string) => {
        if (key !== 'chatId') {
          cloned.append(key, value);
        }
      });
      if (existingChatId != null) {
        cloned.append('chatId', existingChatId.toString());
      }
      // Pass the active jurisdiction (global region) with every draft request,
      // matching enviro-chat's classify_route `region` form field.
      const region = store.jurisdiction?.activeId;
      if (region) {
        cloned.append('region', region);
      }
      return cloned;
    })();

    const slot = SLOT_BY_ENVIRO_NUMBER[enviroNumber] ?? enviroNumber;
    const modelName = getModelName(enviroNumber);
    const header = `EnviroAI Model Response #${slot} - ${modelName}\n\n`;
    const signal = draftAbortRef.current?.signal;

    const slotKey = slot === 1 ? 'a1' : slot === 2 ? 'a2' : 'a3';
    let acc = '';
    let flushTimer: ReturnType<typeof setTimeout> | null = null;
    let settled = false;

    // Live streaming render: update the `messages` state directly with a plain
    // functional updater (a fresh array). Going through addAnswer here does not
    // work — it calls setMessages inside a setNewChat updater, and React 16 does
    // not reliably commit those nested updates mid-stream, so the tokens would
    // only appear once the stream completes. The chat store is persisted via
    // addAnswer once, on `done`.
    const renderStream = () => {
      setMessages((prev: any[]) => {
        if (!prev || !prev.length) { return prev; }
        const targetIdx = index < prev.length ? index : prev.length - 1;
        const item = prev[targetIdx];
        if (!item) { return prev; }
        const next = [...prev];
        next[targetIdx] = { ...item, q: { ...item.q, [slotKey]: `${header}${acc}` } };
        return next;
      });
    };
    const clearFlush = () => {
      if (flushTimer) {
        clearTimeout(flushTimer);
        flushTimer = null;
      }
    };
    // Coalesce token bursts into a ~50ms flush so React re-renders stay cheap.
    const scheduleFlush = () => {
      if (flushTimer) return;
      flushTimer = setTimeout(() => {
        flushTimer = null;
        renderStream();
      }, 50);
    };
    // Increment the completed-draft counter exactly once per draft; drives the
    // save-button gating and the "still loading" loader state.
    const markLoaded = () => {
      if (settled) return;
      settled = true;
      clearFlush();
      dispatch(updateGlobalFlags({ loadedDrafts: [...(store.globalFlags.loadedDrafts || []), enviroNumber] }));
      setTotalDraftLoaded(count => count + 1);
      if (currentChat !== null) {
        setIsChatUpdate(false);
      }
    };

    try {
      await streamDraftResponse(
        url,
        requestData,
        {
          onMeta: (chatId) => {
            if (chatId != null) chatIdsRef.current[enviroNumber] = chatId;
          },
          onToken: (chunk) => {
            if (signal?.aborted) return;
            acc += chunk;
            scheduleFlush();
          },
          onDone: (chatId) => {
            if (signal?.aborted || settled) return;
            if (chatId != null) chatIdsRef.current[enviroNumber] = chatId;
            if (!acc) {
              handleDraftDataNotFound(enviroNumber, index);
            } else {
              // Persist the final answer into the chat store (newChat/savedChats).
              addAnswer(`${header}${acc}`, slot, index);
            }
            markLoaded();
          },
          onError: (message) => {
            if (signal?.aborted || settled) return;
            if (acc) {
              addAnswer(`${header}${acc}`, slot, index);
              showToastMessage({ type: 'error', title: modelName, description: message || 'Something went wrong' });
            } else {
              handleDraftDataNotFound(enviroNumber, index);
            }
            markLoaded();
          },
        },
        signal
      );
    } catch (error: any) {
      clearFlush();
      if (error?.name === 'AbortError' || signal?.aborted) return;
      // Retry once on a server error, mirroring the previous 500 behaviour.
      if (error?.message === 'HTTP 500') {
        getDraftData(url, enviroNumber, index, formData);
        return;
      }
      const err = error?.message === 'FILE_TOO_LARGE'
        ? { code: 413, name: 'Error', message: 'File is too large.' }
        : error;
      handleDraftDataNotFound(enviroNumber, index, err);
      markLoaded();
    }
  };

  // UI-only: surface the failure and drop a placeholder into the model's fixed
  // slot. Counter/loadedDrafts bookkeeping is handled by the caller (markLoaded).
  const handleDraftDataNotFound = (enviroNumber: number, index: number, error?: any) => {
    const slot = SLOT_BY_ENVIRO_NUMBER[enviroNumber] ?? enviroNumber;
    const modelName = getModelName(enviroNumber);
    if (error && (error.code === 'ECONNABORTED' || error.code === 413)) {
      handleErrorMessage(error);
    } else {
      showToastMessage({ type: 'error', title: modelName, description: 'Data Not Found' });
    }
    addAnswer(`EnviroAI Model Response #${slot} - ${modelName}\n\nData Not Found`, slot, index);
  };

  /** Load other drafts */
  const otherDrafts = async (searchedQuestion: string, remainingDrafts?: number[]) => {
    let total = 0;
    let oldQuestion = '';
    setMainQuestion((prevState: string) => {
      oldQuestion = prevState;
      return prevState;
    });
    setTotalDraftLoaded((prevState: number) => {
      total = prevState;
      return prevState;
    });

    if (searchedQuestion !== oldQuestion || total > 3) {
      return;
    }
    setShowSpinner('view_draft_loader');

    const index = messages.length;
    const question = searchedQuestion || messages[index - 1].q.title;

    let chatStatus = '';
    if (currentChat != null && currentChat >= 0) {
      chatStatus = 'ongoing';
    } else {
      chatStatus = newChat && newChat.chatData && newChat.chatData.length > 0 ? 'ongoing' : 'new';
    }

    const formData = new FormData();
    formData.append('q', question);
    formData.append('chatStatus', chatStatus);

    const localUserId = localStorage.getItem('uid');
    if (localUserId) {
      formData.append('userId', encodeURIComponent(localUserId));
    }
    const enviroChatApi = process.env.REACT_APP_ENVIROCHAT_API_ENDPOINT;
    const getApiEndPoint = (draft: number) => {
      if (draft === 1) { return `${enviroChatApi}/gemini/get_pred`; }
      if (draft === 2) { return `${enviroChatApi}/gpt/get_pred`; }
      return `${enviroChatApi}/claude/get_pred`;
    };

    const apiEndPoints = [
      remainingDrafts ? getApiEndPoint(remainingDrafts[0]) : `${enviroChatApi}/gpt/get_pred`,
      remainingDrafts ? getApiEndPoint(remainingDrafts[1]) : `${enviroChatApi}/claude/get_pred`,
    ];

    const method = Array.isArray(files) && files.length ? 'POST' : 'GET';
    const draft1 = remainingDrafts ? remainingDrafts[0] : 2;
    const draft2 = remainingDrafts ? remainingDrafts[1] : 3;

    if (method === 'POST') {
      files.forEach((file: any) => formData.append('files', file));
    }

    getDraftData(apiEndPoints[0], draft1, index, formData);
    getDraftData(apiEndPoints[1], draft2, index, formData);
  };

  // Helper function to convert Blob to base64
  const blobToBase64 = (blob: any) => {
    return new Promise((resolve, reject) => {
      const reader: any = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  /**
   * Add attachment files in search input
   * @param event
   * @returns
   */
  const appendAttachmentFiles = async (selectedFiles: File[]) => {
    if (!selectedFiles.length) { return; }

    const updatedFiles = [...files, ...selectedFiles];
    setFiles(updatedFiles);
    setFilePreviews([]); // Reset file previews

    // Process each file for previews
    const previews = await Promise.all(
      updatedFiles.map(async (file: any) => {
        if (IMAGE_MIME_TYPES.includes(file.type)) {
          const base64 = await blobToBase64(file);
          return { preview: base64, name: file.name, type: file.type };
        }
        return { preview: URL.createObjectURL(file), name: file.name, type: file.type };
      })
    );

    setFilePreviews(previews);
  };

  /**
   * Picker adapter. The input's `accept` filter is advisory — the file dialog
   * can be switched to "All files" — so validate here as well as on drop.
   */
  const uploadAttachment = async (event: any) => {
    if (!event || !event.target) { return; }

    const selectedFiles = Array.from(event.target.files) as File[];
    event.target.value = ''; // Reset the file input value to allow selecting the same file again

    const { accepted, rejected } = partitionFiles(selectedFiles);
    notifyRejectedFiles(rejected);
    await appendAttachmentFiles(accepted);
  };

  // Drop anywhere on the page to attach, matching the paperclip's own lockout.
  const { isDragging } = useFileDrop({
    onFiles: appendAttachmentFiles,
    onReject: notifyRejectedFiles,
    disabled: isExxonMobileEmail(context),
  });

  /**
   * Remove attachment files in search input
   * @param index
   */
  const removeAttachment = (index: number) => {
    setFiles((prevFiles: any) => prevFiles.filter((_: any, i: number) => i !== index));
    setFilePreviews((prevPreviews: any) => prevPreviews.filter((_: any, i: number) => i !== index));
  };

  useEffect(() => {
    const stateFiles = (location.state as any)?.files as File[] | undefined;
    if (!Array.isArray(stateFiles) || !stateFiles.length) return;
    const processFiles = async () => {
      const previews = await Promise.all(
        stateFiles.map(async (file: File) => {
          if (IMAGE_MIME_TYPES.includes(file.type)) {
            const base64 = await blobToBase64(file);
            return { preview: base64, name: file.name, type: file.type };
          }
          return { preview: URL.createObjectURL(file), name: file.name, type: file.type };
        })
      );
      setFiles(stateFiles);
      setFilePreviews(previews);
    };
    void processFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Search default draft */
  const searchInBot = async (e: any, isSuggestedQuestion: boolean) => {
    if (currentChat !== null) {
      setIsChatUpdate(true);
    }
    // Reset loadedDrafts on new question search.
    dispatch(updateGlobalFlags({ loadedDrafts: [] }));
    const question = isSuggestedQuestion ? e : e.key === 'Enter' ? e.target.value : null;
    if (!question) { return; }
    // Track analytics event for the search query
    ReactGA.event({
      category: 'Search',
      action: 'Enviro Chat Query',
      label: question
    });

    initializeSearch(question);

    const index = messages.length;
    const chatStatus = determineChatStatus();
    const formData = buildFormData(question, chatStatus);

    // Decide whether to use 'POST' or 'GET' based on the presence of files
    getDraftData(
      `${process.env.REACT_APP_ENVIROCHAT_API_ENDPOINT}/gemini/get_pred`,
      1,
      index,
      formData
    );
    setTimeout(() => {
      otherDrafts(question);
    });
  };

  searchInBotRef.current = searchInBot;

  useEffect(() => {
    if (consumedHomepageQueryRef.current) {
      return;
    }
    const query = new URLSearchParams(location.search).get('q');
    if (!query) {
      return;
    }
    consumedHomepageQueryRef.current = true;
    setSearchWord(query);
    const run = searchInBotRef.current;
    if (run) {
      void run(query, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run for ?q= URL; always invoke latest searchInBot via ref
  }, [location.search]);

  // Helper function to initialize search parameters and UI states
  const initializeSearch = (question: string) => {
    // Cancel any streams still running for the previous question.
    draftAbortRef.current?.abort();
    draftAbortRef.current = new AbortController();
    setFilePreviews([]);
    setTotalDraftLoaded(0);
    setSearchWord('');
    textareaRef.current.style.height = '34px';
    setIsSearchMultiline(false);
    addQuestion(question);
    setMainQuestion(question);
    setTimeout(() => scrollToBottom(), 100);
  };

  // Helper function to determine the current chat status
  const determineChatStatus = () => {
    if (currentChat !== null && currentChat >= 0) {
      return 'ongoing';
    }
    return newChat && newChat.chatData && newChat.chatData.length > 0 ? 'ongoing' : 'new';
  };

  // Helper function to build the form data for 'POST' requests
  const buildFormData = (question: string, chatStatus: string) => {
    const formData = new FormData();
    formData.append('q', question);
    formData.append('chatStatus', chatStatus);

    const localUserId = localStorage.getItem('uid');
    if (localUserId) {
      formData.append('userId', encodeURIComponent(localUserId));
    }

    if (Array.isArray(files) && files.length) {
      files.forEach((file) => {
        formData.append('files', file);
      });
    }

    return formData;
  };

  /** Pin scroller to bottom  */
  const scrollToBottom = () => {
    const out = document.getElementById('out');

    if (out) {
      const scrollOptions: ScrollToOptions = {
        top: out.scrollHeight - out.clientHeight,
        behavior: 'smooth',
      };

      out.scrollTo(scrollOptions);
    }
  };

  /** Integrate create savChat api */
  const saveChatsHandler = async () => {
    if (currentChat !== null) {
      setIsChatUpdate(false);
    }
    try {
      setShowSpinner('save_btn_loader');

      // Include chatIds in the payload when saving
      const chatIdsToSave = { ...chatIdsRef.current };

      const chatDataToSave = savedChatId && currentChat !== null
        ? { type: 'update', id: savedChatId, record: { ...savedChats[currentChat].payload, chatData: messages, chatIds: chatIdsToSave } }
        : { type: 'create', record: { ...newChat, chatIds: chatIdsToSave } };

      const response: any = await saveChats(chatDataToSave);

      if (response && response.data) {
        const createdId = chatDataToSave.type === 'create' ? response.data.id : null;
        if (!savedChatId) {
          setSavedChatId(response.data.id);
        }

        showToastMessage({
          type: 'success',
          actionType: chatDataToSave.type === 'create' ? 'create' : 'update',
          title: `Data ${chatDataToSave.type === 'create' ? 'created' : 'updated'} successfully.`,
          description: `${chatDataToSave.type === 'create' ? 'Created' : 'Updated'} chat: ${chatDataToSave.record.mainTitle}`
        });
        setNewChat(null);
        const refreshedChats: any = await getSavedChats({ type: 'getAll' });

        // After creating the first chat, select it so subsequent prompts append to the same conversation.
        if (createdId && Array.isArray(refreshedChats)) {
          const newIndex = refreshedChats.findIndex((c: any) => c && c.id === createdId);
          if (newIndex >= 0) {
            setCurrentChat(newIndex);
          }
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        handleErrorMessage(error);
      }
    } finally {
      setShowSpinner(null);
    }
  };

  /** Delete Chat */
  const deleteChatHandler = async () => {
    try {
      setShowDeleteConfitmationPopup(false);
      setShowSpinner('delete_btn_loader');
      if (!(savedChatId && currentChat !== null)) {
        return;
      }

      const chatTitle = currentChat !== null ? savedChats[currentChat].payload.mainTitle : '';
      const response = await saveChats({
        type: 'remove',
        id: savedChatId
      });

      if (response && response.data) {
        setSavedChatId(null);
        setNewChat(null);
        setIsNewChatEnable(true);
        showToastMessage({ type: 'success', actionType: 'delete', title: 'Data deleted successfully.', description: `Deleted chat: ${chatTitle}` });
        await getSavedChats({ type: 'getAll' });
      }
    } catch (error) {
      if (error instanceof Error) {
        handleErrorMessage(error);
      }
    } finally {
      setShowSpinner(null);
    }
  };

  /**
   * Select favorite from prompt modal
   * @param index
   */
  function onFavoriteClick(index: number) {
    if (!Array.isArray(favorites)) {
      return;
    }
    const updatedFavorites = favorites.map((item: any, i: number) =>
      index === i ? { ...item, isFavorite: item.hasOwnProperty('isFavorite') ? !item.isFavorite : true } : item
    );

    setFavorites(updatedFavorites);
  }

  /**
   * Save favorites
   */
  const saveFavorites = async () => {
    try {
      setShowSpinner('save_favorites');

      const promptData = favorites.filter(data => data.id || data.isFavorite);
      promptData.forEach(async (item: any, i: number) => {
        if (item.id && item.isFavorite) {
          return;
        } else {
          const actionType = item.id ? 'remove' : 'create';
          await createRemovePrompt(actionType, item, i);
        }
      });

      const successMessage = 'Prompts Saved Successfully.';
      showToastMessage({ type: 'success', title: successMessage });

    } catch (error) {
      if (error instanceof Error) {
        handleErrorMessage(error);
      }
    } finally {
      setShowSpinner(null);
      setShowQuestionsPopup(false);
    }
  };

  const createRemovePrompt = async (type: string, prompt: any, i: number) => {
    const response: any = await savePrompts({
      type,
      id: prompt.id,
      record: { promptData: [prompt.question] }
    });

    if (response && response.data) {
      const updatedFavorites = favorites;
      updatedFavorites[i].id = type === 'create' ? response.data.id : '';
      setFavorites(updatedFavorites);
    }
  };

  const promptChangesHandler = (promptsData: any) => {
    // Flatten the nested arrays using flat()
    const promptsList = promptsData.flatMap((items: any) => items.payload.promptData).flat(2);
    setPrompts(promptsList);
    dispatch(updateQuickPrompts(promptsList));
  };

  const handleNavigation = (previousPage: string, searchQuery: string, searchType: string) => {
    // Set the previous page for return navigation
    dispatch(updateGlobalFlags({ previousPage }));

    // Save chat data for display when navigating back to the myenviroai page
    const chatDataToSave = currentChat !== null && currentChat >= 0
      ? { chatData: [...messages], mainTitle: mainQuestion, id: savedChatId, chatIds: { ...chatIdsRef.current } }
      : newChat ? { ...newChat, chatIds: { ...chatIdsRef.current } } : newChat;
    dispatch(updateSavedChatData({ newChat: chatDataToSave, currentChat }));

    // Navigate to the desired page based on the search query and type
    history.push(`/search?q=${searchQuery}&type=${searchType}`);
  };

  const handleSearchClick = async (spinnerType: string, previousPage: string, searchType: string) => {
    setShowSpinner(spinnerType);
    try {
      const response = await axios.post(`${process.env.REACT_APP_ENVIROCHAT_API_ENDPOINT}/prompt_keywords`, { prompt: mainQuestion });
      handleNavigation(previousPage, response.data, searchType);
    } catch (error) {
      // tslint:disable-next-line:no-console
      console.log('Error:', error);  // Improved error logging
      handleNavigation(previousPage, mainQuestion, searchType);
    } finally {
      setShowSpinner(null);
    }
  };

  const onAgencyDocumentClick = () => {
    handleSearchClick('agency_documents', 'myenviroaiToAgency', 'default');
  };

  const onInternetResultsClick = () => {
    handleSearchClick('internet_result', 'myenviroaiToInternetResults', 'ALL');
  };

  const onFacilityMapClick = () => {
    handleNavigation('myenviroaiToFacility', '', ''); // Empty query and type as they are not needed for this navigation
    history.push(`/my-tundra`);  // Navigate to the Facility map page
  };

  const onClickPrintBtn = () => {
    // Function to sanitize text by removing HTML tags
    const sanitizeText = (text: string) => text.replace(/<\/?[^>]+(>|$)/g, '');

    // Function to split text into lines and handle page breaks
    const splitLines = (jspdf: jsPDF, text: string, y: number) => {
      const pageHeight = jspdf.internal.pageSize.getHeight();
      const textWidth = 180; // Width of the text block
      const margin = 10; // Margin from the edge of the page
      const textLines = jspdf.splitTextToSize(text, textWidth);
      const lineHeight = jspdf.getTextDimensions(textLines[0]).h; // Height of a single line

      let startY = y; // Initial Y position

      textLines.forEach((line: any) => {
        if (startY + lineHeight > pageHeight - margin) {
          jspdf.addPage(); // Add a new page
          startY = margin; // Reset startY for the new page
        }
        jspdf.text(line, margin, startY); // Add the line to the PDF
        startY += lineHeight; // Move the Y position for the next line
      });

      return startY;
    };

    // Create a new jsPDF instance
    const pdf = new jsPDF({ format: 'a4', orientation: 'portrait' });

    // Determine the title for the PDF
    let pdfTitle = 'Chat transcript';
    const chatData = savedChatId ? messages : newChat.chatData;

    if (chatData.length > 0) {
      pdfTitle = getPdfTitle(chatData[0]);
    }

    chatData.forEach((data: any, i: number) => {
      pdf.setFontSize(12);
      const questionHeight = splitLines(pdf, sanitizeText(`Q.${i + 1} ${data.q.title}`), 10);

      pdf.setFontSize(10);
      splitLines(pdf, sanitizeText(`Ans.1 ${data.q.a1}`), questionHeight + 4);
      pdf.addPage();
      splitLines(pdf, sanitizeText(`Ans.2 ${data.q.a2}`), 10);
      pdf.addPage();
      splitLines(pdf, sanitizeText(`Ans.3 ${data.q.a3}`), 10);

      if (i < chatData.length - 1) {
        pdf.addPage();
      }
    });

    // Sanitize and save the PDF
    pdfTitle = sanitizeTitle(pdfTitle);
    pdf.save(`${pdfTitle}.pdf`);
  };

  // Function to extract and format the PDF title
  const getPdfTitle = (data: any) => {
    const words = (data.q.title || 'Chat transcript').split(' ');
    return words.length >= 2 ? words.slice(0, 2).join(' ') : words.join(' ');
  };

  // Function to sanitize the title for valid filename characters
  const sanitizeTitle = (title: string) => title.replace(/[^a-zA-Z0-9 \-_]+/g, '');

  // Handle catch error
  function handleErrorMessage(error: Error) {
    const { name, message } = error;
    showToastMessage({ type: 'error', title: name, description: message });
  }

  /** Delete Chat Confirmation Modal */
  function DeleteChatConfirmationModal(modalProps: any) {
    return (
      <Modal
        {...modalProps}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        className="cmn-saved-prompts-modal"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            Delete Chat Confirmation
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure, you want to delete <strong>{currentChat !== null ? savedChats[currentChat].payload.mainTitle : ''}</strong> chat?
        </Modal.Body>
        <Modal.Footer>
          <Button className="cmn-btn-mint" onClick={deleteChatHandler}>
            Delete
          </Button>
          <Button className="cmn-btn-mint-hover-only" onClick={modalProps.onHide}>Close</Button>
        </Modal.Footer>
      </Modal>
    );
  }

  /** Save prompts popup */
  function QuestionsListModal(modalProps: any) {
    const isSaveEnable = Array.isArray(favorites) ? favorites.some((o: any) => o.isFavorite || o.id) : false;

    return (
      <Modal
        {...modalProps}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        className="cmn-saved-prompts-modal"
        style={{ zIndex: 99999 }}
        centered
        animation={false}
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            Add To Saved Prompts
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {Array.isArray(favorites) && favorites.map((item: any, i: number) => (
            <Card key={i} text="primary" className="mb-2">
              <Card.Body className="d-flex justify-content-between align-items-center">
                <Card.Title className="mb-0">{item.question}</Card.Title>
                <IconButton
                  onClick={() => onFavoriteClick(i)}
                  aria-label="fav-icon"
                  component="span"
                >
                  {item.isFavorite ? (
                    <FillStarIcon style={{ color: '#faaf00' }} />
                  ) : (
                    <EmptyStarIcon style={{ color: '#faaf00' }} />
                  )}
                </IconButton>
              </Card.Body>
            </Card>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button className="cmn-btn-mint" onClick={saveFavorites} disabled={!isSaveEnable}>
            Save
            {showSpinner === 'save_favorites' && (
              <Spinner animation="border" variant="light" size="sm" className="ml-2" />
            )}
          </Button>
          <Button className="cmn-btn-mint-hover-only" onClick={modalProps.onHide}>Cancel</Button>
        </Modal.Footer>
      </Modal>
    );
  }

  /**
   * Default New Chat Screen
   * @returns New Chat Screen
   */
  const isLandingChat = !messages.length;

  const DefaultNewChatLayout = () => {
    if (showSpinner && showSpinner === 'load_question_list') {
      return (
        <HeroWelcome $landing={isLandingChat}>
          <div className="d-flex justify-content-center align-items-center w-100 py-5">
            <Spinner animation="border" style={{ color: '#34d399' }} />
          </div>
        </HeroWelcome>
      );
    }

    return (
      <HeroWelcome $landing={isLandingChat}>
        <HeroTagline>Climbing toward environmental superintelligence</HeroTagline>
        <HeroHeading>
          EnviroMulti-Model
          <HeroHeadingAccent>Ask anything</HeroHeadingAccent>
        </HeroHeading>
      </HeroWelcome>
    );
  };

  /** Three dot message loader */
  // const MessageLoader = () => (
  //   <div className="loader-message d-flex gap-2 align-items-center justify-content-center">
  //     <p className="mt-2 mb-0" style={{fontSize: '14px',fontFamily: 'Roboto, sans-serif' }}>Accessing the world's most powerful environmental brain. Stand by as it sifts through millions of documents and the internet to produce your response</p>
  //     <div className="typing">
  //       <span></span>
  //       <span></span>
  //       <span></span>
  //     </div>
  //   </div>
  // );

  /** Saved chat messages area */
  // const ChatMessages = () => {
  //   if (isNewChatEnable) {
  //     return null;
  //   }

  //   return (
  //     <>
  //       {messages.map((item: any, i: number) => (
  //         <div key={i}>
  //           <ul className="mb-0 question">
  //             <li className="clearfix question">
  //               <div className="float-right">
  //                 <div className="message other-message">
  //                   {item.q.title}
  //                 </div>
  //                 <p className="message-datetime">{ item.timestamp ? moment.unix(item.timestamp).format('DD/MM/YYYY HH:mm') : moment.unix(Math.floor(Date.now() / 1000)).format('DD/MM/YYYY HH:mm')}</p>
  //               </div>
  //             </li>
  //           </ul>
  //           <ul className="mb-0 d-flex justify-content-between">
  //             {[item.q.a1, item.q.a2, item.q.a3].map((answer, index) => (
  //               <li key={index} className="clearfix answer">
  //                 {loading === `enviro${index + 1}` && i === messages.length - 1 ? (
  //                   <MessageLoader />
  //                 ) : (
  //                   answer && (
  //                     <div>
  //                       <div
  //                         className={`message my-message ${((loading === `enviro${index + 2}` || index >= 2) && i === messages.length - 1) ? 'anim' : ''}`}
  //                         dangerouslySetInnerHTML={generateHyperlink(answer, index)}
  //                       />
  //                       <p className="message-datetime">{ item.timestamp ? moment.unix(item.timestamp).format('DD/MM/YYYY HH:mm') : moment.unix(Math.floor(Date.now() / 1000)).format('DD/MM/YYYY HH:mm')}</p>
  //                     </div>
  //                   )
  //                 )}
  //               </li>
  //             ))}
  //           </ul>
  //         </div>
  //       ))}
  //     </>
  //   );
  // };

  // Adjust the height of the textarea dynamically
  const adjustTextareaHeight = useCallback((text?: string) => {
    if (textareaRef.current) {
      const maxHeight = 280;
      const trimmed = (text ?? searchWord ?? '').trim();
      textareaRef.current.style.height = '34px';
      const scrollHeight = textareaRef.current.scrollHeight;
      const nextHeight = Math.min(Math.max(scrollHeight, 34), maxHeight);
      textareaRef.current.style.height = `${nextHeight}px`;
      textareaRef.current.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
      setIsSearchMultiline(trimmed.includes('\n') || nextHeight > 50);
    }
  }, [searchWord]);

  // Handle input change
  const handleSearchInput = (event: any) => {
    setSearchWord(event.target.value);
    adjustTextareaHeight(event.target.value);
  };

  // Adjust the height when the window is resized
  useEffect(() => {
    const handleResize = () => {
      adjustTextareaHeight();
    };

    window.addEventListener('resize', handleResize);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [adjustTextareaHeight]);

  // Abort any in-flight draft streams when the page unmounts.
  useEffect(() => {
    return () => {
      draftAbortRef.current?.abort();
    };
  }, []);

  const onCopyTextHandler = (title: string) => {
    setSearchWord(title);
    setTimeout(() => {
      adjustTextareaHeight();
    });
  };

  return (
    <>
      <FileDropOverlay visible={isDragging} />
      {showSavePrompt &&
        <SavedPrompts context={props.context} closeSavedPrompt={() => setShowSavePrompt(false)} onCopyText={(title: string) => onCopyTextHandler(title)} onPromptsChange={promptChangesHandler} />}
      {showSaveLibrary &&
        <SavedLibrary context={props.context} closeSavedLibrary={() => setShowSaveLibrary(false)} libraryType={1} onCopyText={(title: string) => onCopyTextHandler(title)} />}
      {showSavePrivateLibrary &&
        <SavedLibrary context={props.context} closeSavedLibrary={() => setShowSavePrivateLibrary(false)} libraryType={2} onCopyText={(title: string) => onCopyTextHandler(title)} />}

      {showProfile && <Profile closeProfile={() => setShowProfile(false)} onToggleProfile={(profile: string) => setActiveProfile(profile)} />}

      <div className="enviro-ai">

        {/* Header */}
        <nav className="navbar navbar-expand-lg navbar-dark header fixed-top">
          <div className="collapse navbar-collapse justify-content-between" id="navbarNavAltMarkup">
            <div className="navbar-nav">
              <button id="saved-prompts-btn" className="btn btn-outline-light mr-1" onClick={() => setShowSavePrompt(true)}>
                <ClearAllIcon /> Saved Prompts
              </button>
              <button id="profile-btn" className="btn btn-outline-light mr-1" onClick={() => setShowProfile(true)}>
                <PersonIcon /> Profile
              </button>
              <button
                id="library-btn"
                className={
                  isExxonMobileEmail(context)
                    ? 'btn btn-outline-light mr-1 btn-disable'
                    : 'btn btn-outline-light mr-1'
                }
                onClick={() => {
                  if (!isExxonMobileEmail(context)) {
                    setShowSaveLibrary(true);
                  } else {
                    showToastMessage({
                      type: 'warn',
                      title: 'Warning',
                      description: 'This feature is disabled for ExxonMobil. Please contact the Administrator.',
                    });
                  }
                }}
              >
                <LibraryBooksIcon /> Library
              </button>
              <button
                id="library-btn"
                className="btn btn-outline-light"
                onClick={() => setShowSavePrivateLibrary(true)}
              >
                <LibraryBooksIcon /> Private Library
              </button>
            </div>
            <img src={HeaderEnviroAILogo} alt="EnviroAI Logo" />
            <button id="new-chat-btn" className="btn btn-outline-light" onClick={() => { setCurrentChat(null); dispatch(updateSavedChatData({})); resetChatState(); }}>
              <EditIcon fontSize="small" /> New Chat
            </button>
          </div>
        </nav>

        {/* Chat area */}
        <div className="row clearfix">
          {showDeleteConfitmationPopup && <DeleteChatConfirmationModal show={showDeleteConfitmationPopup} onHide={() => setShowDeleteConfitmationPopup(false)} />}
          <QuestionsListModal
            show={showQuestionsPopup}
            onHide={() => setShowQuestionsPopup(false)}
          />
          <LibraryQuestionsListModal
            context={props.context}
            showLibraryQuestionsPopup={showLibraryQuestionsPopup}
            setShowLibraryQuestionsPopup={setShowLibraryQuestionsPopup}
            libraryFavorites={libraryFavorites}
            setLibraryFavorites={setLibraryFavorites}
          />
          <div className="col-lg-12">
            <div className="card chat-app">
              <ChatTitleList savedChats={savedChats} setTotalSavedChats={setTotalSavedChats} totalSavedChats={totalSavedChats}
                showSpinner={showSpinner} setShowSpinner={setShowSpinner} setSavedChats={setSavedChats} getSavedChats={getSavedChats}
                editableIndex={editableIndex} currentChat={currentChat} toggleChat={toggleChat} setEditableIndex={setEditableIndex} />
              <div className={`chat enviro-mountain-chat${messages.length ? ' chat--has-messages' : ''}`} id="mychat">
                <MountainBackdrop />
                <MountainOverlay />
                <MountainContentWrap $landing={isLandingChat}>
                  {showSpinner && showSpinner === 'load_current_chat' ? (
                    <div className="d-flex justify-content-center my-3 w-100 align-items-center h-100 quote-without-author">
                      <div className="text-center">
                        <Spinner animation="border" variant="primary" />
                        <SpinnerText>Loading Chats...</SpinnerText>
                      </div>
                    </div>) :
                    (messages.length ?
                      <div className="chat-history-wrapper chat-on-mountain">
                        <div className="chat-history chat-on-mountain" id="out">
                          {/* Fix re-animation issue by use html instead ChatMessages component */}
                          {messages.map((item: any, i: number) => (
                            <div key={i}>
                              <ul className="mb-0 question">
                                <li className="clearfix question">
                                  <div>
                                    {item.attachFiles && item.attachFiles.map((file: any, index: number) => {
                                      return IMAGE_MIME_TYPES.includes(file.type) ? (
                                        <div className="preview-img mr-1">
                                          <img src={file.preview} alt={file.name} width="200" />
                                        </div>
                                      ) : (
                                        <div className="preview-file">
                                          <FileCopy color="primary" />
                                          <div>
                                            <p>{file.name}</p>
                                            <p>{file.type}</p>
                                          </div>
                                        </div>
                                      );
                                    })}
                                    <div className="query-content">
                                      <p className="mb-0 message other-message">{item.q.title}</p>
                                    </div>
                                    <p className="message-datetime">{item.timestamp ? moment.unix(item.timestamp).format('DD/MM/YYYY HH:mm') : moment.unix(Math.floor(Date.now() / 1000)).format('DD/MM/YYYY HH:mm')}</p>
                                  </div>
                                </li>
                              </ul>
                              <ul className="mb-0 d-flex justify-content-between">
                                {[item.q.a1, item.q.a2, item.q.a3].map((answer, index) => (
                                  <li key={index} className="clearfix answer">
                                    {totalDraftLoaded < 3 && !answer && i === messages.length - 1 ? (
                                      <MessageLoader />
                                    ) : (
                                      answer && (
                                        <div>
                                          <div className={'message my-message'}>
                                            {renderAnswerContent(answer)}
                                          </div>
                                          <p className="message-datetime">{item.timestamp ? moment.unix(item.timestamp).format('DD/MM/YYYY HH:mm') : moment.unix(Math.floor(Date.now() / 1000)).format('DD/MM/YYYY HH:mm')}</p>
                                        </div>
                                      )
                                    )}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                          {/* <ChatMessages /> */}
                        </div>
                      </div> : <DefaultNewChatLayout />)
                  }

                  {/* User search input with quick buttons */}
                  <ChatInputDock $compact={Boolean(messages.length)} $landing={isLandingChat}>
                    <div className="chat-message-wrapper" style={{ height: 'auto' }}>
                      <div className={`chat-message clearfix ${messages.length ? 'message-chatSearch' : 'chatSearch'}`}>
                        {/* Active profile */}
                        {activeProfile && (newChat || isNewChatEnable) &&
                          <div className="d-flex justify-content-center my-2">
                            <p className="alert active-profile-label">
                              {/* <InfoIcon className="text-black" /> */}
                              <span className="dot-icon"></span>
                              <span>{activeProfile}</span>
                              <HelpIcon data-tip="Active Profile" fontSize="small" id="profile-help" style={{ cursor: 'pointer' }} />
                            </p>
                          </div>
                        }

                        {/* New chat quick buttons */}
                        {messages.length ?
                          <>
                            <div className="quick-buttons">
                              {/* {showDraftBtn &&
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip id="view-draft-btn" className="mb-2">Click to view more drafts</Tooltip>}
                            >
                              <button
                                id="view-draft-btn"
                                className="btn btn-primary rounded-pill mr-2 px-4"
                                disabled={showSpinner === 'save_prompt_btn_loader' || showSpinner === 'save_btn_loader' || showSpinner === 'view_draft_loader' || showSpinner === 'save_library_btn_loader' || !!loading}
                                onClick={() => otherDrafts('')}>
                                  <DraftsIcon /> View Other Drafts
                                  {showSpinner === 'view_draft_loader' && <Spinner animation="border" variant="light" size="sm" className="ml-2" />}
                              </button>
                            </OverlayTrigger>
                          } */}
                              {mainQuestion &&
                                <button
                                  data-tip="Click to view agency results"
                                  data-for="quick-button-tooltip"
                                  id="agency-doc-btn"
                                  className="btn btn-primary rounded-pill mr-2 px-4"
                                  disabled={showSpinner === 'agency_documents'}
                                  onClick={onAgencyDocumentClick}
                                >
                                  <DescriptionIcon /> Agency Documents
                                  {showSpinner === 'agency_documents' && <Spinner animation="border" variant="light" size="sm" className="ml-2" />}
                                </button>
                              }
                              {mainQuestion &&
                                <button
                                  data-tip="Click to view internet results"
                                  data-for="quick-button-tooltip"
                                  id="internet-result-btn"
                                  className="btn btn-primary rounded-pill mr-2 px-4"
                                  disabled={showSpinner === 'internet_result'}
                                  onClick={onInternetResultsClick}>
                                  <DescriptionIcon /> Internet Results
                                  {showSpinner === 'internet_result' && <Spinner animation="border" variant="light" size="sm" className="ml-2" />}
                                </button>
                              }
                              {mainQuestion &&
                                <button
                                  data-tip="Click to view facilities maps"
                                  data-for="quick-button-tooltip"
                                  id="internet-result-btn"
                                  className="btn btn-primary rounded-pill mr-2 px-4"
                                  onClick={onFacilityMapClick}>
                                  <MapIcon /> Facilities Maps
                                </button>
                              }
                              {(showSaveBtn && newChat) &&
                                <button
                                  data-tip="Click to save chats"
                                  data-for="save-button-tooltip"
                                  id="save-btn"
                                  className="btn btn-primary rounded-pill mr-2 px-4"
                                  disabled={showSpinner === 'save_prompt_btn_loader' || showSpinner === 'save_btn_loader' || showSpinner === 'view_draft_loader' || showSpinner === 'save_library_btn_loader' || totalDraftLoaded < 3}
                                  onClick={saveChatsHandler}>
                                  <SaveIcon /> Save
                                  {showSpinner === 'save_btn_loader' && <Spinner animation="border" variant="light" size="sm" className="ml-2" />}
                                  <StyledTooltip id="save-button-tooltip" place="bottom" effect="solid" />
                                </button>
                              }
                              {(showSavePromptBtn && newChat) &&
                                <button
                                  data-tip={
                                    isExxonMobileEmail(context)
                                      ? 'This Feature is disabled for ExxonMobil. Please contact the Administrator.'
                                      : 'Save Prompts'
                                  }
                                  data-for="prompt-button-tooltip"
                                  id="save-prompt-btn"
                                  className={
                                    isExxonMobileEmail(context)
                                      ? 'btn btn-primary rounded-pill mr-2 px-4 btn-disable'
                                      : 'btn btn-primary rounded-pill mr-2 px-4'
                                  }
                                  disabled={showSpinner === 'save_prompt_btn_loader' || showSpinner === 'save_btn_loader' || showSpinner === 'view_draft_loader' || showSpinner === 'save_library_btn_loader' || totalDraftLoaded < 3}
                                  onClick={() => {
                                    if (isExxonMobileEmail(context)) {
                                      showToastMessage({
                                        type: 'warn',
                                        title: 'Warning',
                                        description:
                                          'This Feature is disabled for ExxonMobil. Please contact the Administrator.',
                                      });
                                    } else {
                                      setShowQuestionsPopup(true);
                                    }
                                  }}
                                >
                                  <SaveIcon /> Save Prompts
                                  {showSpinner === 'save_prompt_btn_loader' && <Spinner animation="border" variant="light" size="sm" className="ml-2" />}
                                  <StyledTooltip id="prompt-button-tooltip" place="bottom" effect="solid" />
                                </button>
                              }
                              {(showSaveLibraryBtn && newChat) &&
                                <button
                                  id="save-library-btn"
                                  data-tip="Save To Library"
                                  data-for="library-button-tooltip"
                                  className="btn btn-primary rounded-pill mr-2 px-4"
                                  onClick={() => {
                                    setShowLibraryQuestionsPopup(true);
                                  }}
                                  disabled={showSpinner === 'save_prompt_btn_loader' || showSpinner === 'save_btn_loader' || showSpinner === 'view_draft_loader' || showSpinner === 'save_library_btn_loader' || totalDraftLoaded < 3}
                                >
                                  <SaveIcon /> Save To Library
                                  {showSpinner === 'save_library_btn_loader' && <Spinner animation="border" variant="light" size="sm" className="ml-2" />}
                                  <StyledTooltip id="library-button-tooltip" place="bottom" effect="solid" />
                                </button>
                              }
                              {(showSaveBtn || savedChatId) &&
                                <button
                                  data-tip="Click to print chat"
                                  data-for="print-button-tooltip"
                                  id="print-btn"
                                  className="btn btn-primary rounded-pill mr-2 px-4"
                                  disabled={showSpinner === 'save_prompt_btn_loader' || showSpinner === 'save_btn_loader' || showSpinner === 'view_draft_loader' || showSpinner === 'save_library_btn_loader' || totalDraftLoaded < 3}
                                  onClick={onClickPrintBtn}>
                                  <PrintIcon /> Print
                                  <StyledTooltip id="print-button-tooltip" place="bottom" effect="solid" />
                                </button>
                              }
                              {savedChatId && <>
                                <button
                                  data-tip="Update saved chats"
                                  data-for="update-button-tooltip"
                                  id="update-btn" className="btn btn-primary rounded-pill mr-2 px-4"
                                  disabled={showSpinner === 'save_btn_loader' || showSpinner === 'delete_btn_loader' || totalDraftLoaded < 3 || isChatUpdate}
                                  onClick={saveChatsHandler}>
                                  <UpdateIcon /> Update
                                  {showSpinner === 'save_btn_loader' && <Spinner animation="border" variant="light" size="sm" className="ml-2" />}
                                  <StyledTooltip id="update-button-tooltip" place="bottom" effect="solid" />
                                </button>
                                <Button
                                  data-tip="Delete saved chats"
                                  data-for="delete-button-tooltip"
                                  id="delete-btn"
                                  className="btn btn-primary rounded-pill mr-2 px-4"
                                  disabled={showSpinner === 'save_btn_loader' || showSpinner === 'delete_btn_loader' || totalDraftLoaded < 3}
                                  onClick={() => setShowDeleteConfitmationPopup(true)}
                                >
                                  <DeleteIcon /> Delete
                                  {showSpinner === 'delete_btn_loader' && <Spinner animation="border" variant="light" size="sm" className="ml-2" />}
                                  <StyledTooltip id="delete-button-tooltip" place="bottom" effect="solid" />
                                </Button>
                              </>
                              }
                              <StyledTooltip id="quick-button-tooltip" place="bottom" effect="solid" />
                            </div>
                            <div className={`searchbox searchbox--hero${isSearchMultiline ? ' searchbox--multiline' : ''}`}>
                              <div className={filePreviews && filePreviews.length ? 'd-flex mb-1 overflow-auto' : 'd-none'}>
                                {filePreviews.map((file: any, index: number) => {
                                  return IMAGE_MIME_TYPES.includes(file.type) ? (
                                    <div className="preview-img mr-1">
                                      <img src={file.preview} alt={file.name} width="200" />
                                      <Button variant="outline-dark" className="close-button" onClick={() => removeAttachment(index)}><CloseRounded /></Button>
                                    </div>
                                  ) : (
                                    <div className="preview-file">
                                      <FileCopy color="primary" />
                                      <div>
                                        <p>{file.name}</p>
                                        <p>{file.type}</p>
                                      </div>
                                      <Button variant="outline-dark" className="close-button" onClick={() => removeAttachment(index)}><CloseRounded /></Button>
                                    </div>
                                  );
                                })}
                              </div>
                              <div className="searchbox-input-row">
                                <div className="image-upload">
                                  <label
                                    htmlFor="file-input"
                                    style={{ cursor: 'pointer' }}
                                    className={isExxonMobileEmail(context) ? 'text-secondary my-1' : 'text-light my-1'}
                                    data-tip="This feature is disabled for ExxonMobil. Please contact the Administrator."
                                    data-for="exxon"
                                    onClick={() => {
                                      if (isExxonMobileEmail(context)) {
                                        showToastMessage({ type: 'warn', title: 'Warning', description: 'This Feature is disabled for ExxonMobil. Please contact the Administrator.' });
                                      }
                                    }}
                                  >
                                    <AttachFileIcon />
                                  </label>
                                  {isExxonMobileEmail(context) && <StyledTooltip id="exxon" place="top" effect="solid" />}
                                  <input id="file-input" type="file" multiple accept={ACCEPT_ATTR} onChange={uploadAttachment} style={{ display: 'none' }} disabled={isExxonMobileEmail(context)} />
                                </div>
                                <JurisdictionChip />
                                <textarea
                                  ref={textareaRef}
                                  name="message"
                                  value={searchWord}
                                  className="form-control"
                                  style={{ cursor: totalDraftLoaded < 3 ? 'no-drop' : 'text' }}
                                  disabled={totalDraftLoaded < 3}
                                  placeholder="Ask about permits, air quality, water rights, compliance..."
                                  onChange={handleSearchInput}
                                  onKeyDown={(e) => searchInBot(e, false)}
                                  autoFocus
                                />
                                <button
                                  type="button"
                                  className="btn btn-sm btn-link arrow-btn arrow-btn--hero"
                                  disabled={totalDraftLoaded < 3}
                                  onClick={() => searchInBot(searchWord, true)}
                                  aria-label="Send message"
                                >
                                  <SendRounded style={{ fontSize: 24 }} />
                                </button>
                              </div>
                            </div>
                          </>
                          :
                          <div className={`searchbox searchbox--hero${isSearchMultiline ? ' searchbox--multiline' : ''}`}>
                            <div className={filePreviews && filePreviews.length ? 'd-flex mb-1 overflow-auto' : 'd-none'}>
                              {filePreviews.map((file: any, index: number) => {
                                return IMAGE_MIME_TYPES.includes(file.type) ? (
                                  <div className="preview-img mr-1">
                                    <img src={file.preview} alt={file.name} width="200" />
                                    <Button variant="outline-dark" className="close-button" onClick={() => removeAttachment(index)}><CloseRounded /></Button>
                                  </div>
                                ) : (
                                  <div className="preview-file">
                                    <FileCopy color="primary" />
                                    <div>
                                      <p>{file.name}</p>
                                      <p>{file.type}</p>
                                    </div>
                                    <Button variant="outline-dark" className="close-button" onClick={() => removeAttachment(index)}><CloseRounded /></Button>
                                  </div>
                                );
                              })}
                            </div>
                            <div className="searchbox-input-row">
                              <div className="image-upload">
                                <label
                                  htmlFor="file-input"
                                  style={{ cursor: 'pointer' }}
                                  className={isExxonMobileEmail(context) ? 'text-secondary my-1' : 'text-light my-1'}
                                  data-tip="This feature is disabled for ExxonMobil. Please contact the Administrator."
                                  data-for="exxon"
                                  onClick={() => {
                                    if (isExxonMobileEmail(context)) {
                                      showToastMessage({ type: 'warn', title: 'Warning', description: 'This Feature is disabled for ExxonMobil. Please contact the Administrator.' });
                                    }
                                  }}
                                >
                                  <AttachFileIcon />
                                </label>
                                {isExxonMobileEmail(context) && <StyledTooltip id="exxon" place="top" effect="solid" />}
                                <input id="file-input" type="file" multiple accept={ACCEPT_ATTR} onChange={uploadAttachment} style={{ display: 'none' }} disabled={isExxonMobileEmail(context)} />
                              </div>
                              <JurisdictionChip />
                              <textarea
                                ref={textareaRef}
                                name="message"
                                value={searchWord}
                                className="form-control"
                                style={{ cursor: totalDraftLoaded < 3 ? 'no-drop' : 'text' }}
                                disabled={totalDraftLoaded < 3}
                                placeholder="Ask about permits, air quality, water rights, compliance..."
                                onChange={handleSearchInput}
                                onKeyDown={(e) => searchInBot(e, false)}
                                autoFocus
                              />
                              <button
                                type="button"
                                className="btn btn-sm btn-link arrow-btn arrow-btn--hero"
                                disabled={totalDraftLoaded < 3}
                                onClick={() => searchInBot(searchWord, true)}
                                aria-label="Send message"
                              >
                                <SendRounded style={{ fontSize: 24 }} />
                              </button>
                            </div>
                          </div>
                        }
                        {!messages.length && showSpinner !== 'load_question_list' && (
                          <div className="enviro-suggested-prompts-wrap">
                            <SuggestionChipsRow aria-label="Suggested prompts">
                              {(prompts.length ? prompts.slice(0, 4) : FALLBACK_CHIPS).map((label: string, i: number) => (
                                <SuggestionChipBtn key={`${i}-${label.slice(0, 24)}`} type="button" onClick={() => searchInBot(label, true)}>
                                  {label}
                                </SuggestionChipBtn>
                              ))}
                            </SuggestionChipsRow>
                          </div>
                        )}
                        <p className="mb-0 mt-2 text-center enviro-hero-footnote">Environmentally Trained Generative AI</p>
                      </div>
                    </div>
                  </ChatInputDock>
                </MountainContentWrap>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default withAuthContext(EnviroAI);
